import { ExperienceMapper } from "@ninetailed/experience.js-utils"

/**
 * Normalize config so ExperienceMapper schema accepts it: must be an object.
 * Contentful ntConfig is often a JSON string.
 */
function normalizeConfig(raw: unknown): Record<string, unknown> {
  if (raw == null) return {}
  if (typeof raw === "string") {
    try {
      const o = JSON.parse(raw) as Record<string, unknown>
      return o && typeof o === "object" && !Array.isArray(o) ? o : {}
    } catch {
      return {}
    }
  }
  return typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {}
}

/**
 * Map a single Contentful GraphQL experience entry to the shape required by
 * ExperienceMapper.isExperienceEntry.
 *
 * Uses ntExperienceId (the Ninetailed Experience API ID) so the SDK can match
 * the component against Experience API responses during API-driven evaluation.
 */
function toExperienceEntry(experience: any, baselineSysId: string) {
  const id =
    experience?.ntExperienceId != null
      ? String(experience.ntExperienceId)
      : experience?.sys?.id != null
        ? String(experience.sys.id)
        : ""
  const name = experience?.ntName != null ? String(experience.ntName) : ""
  const type =
    experience?.ntType === "nt_experiment"
      ? "nt_experiment"
      : "nt_personalization"

  const rawVariants = experience?.ntVariantsCollection?.items ?? []
  const variants = rawVariants
    .map((variant: any) => {
      const variantId = variant?.sys?.id != null ? String(variant.sys.id) : ""
      return variantId ? { id: variantId, value: variant } : null
    })
    .filter(
      (v: { id: string; value: any } | null): v is { id: string; value: any } =>
        v != null,
    )

  const audience = experience?.ntAudience
    ? {
        id: String(experience.ntAudience.ntAudienceId ?? ""),
        name: experience.ntAudience.ntName ?? undefined,
        description: experience.ntAudience.ntDescription ?? undefined,
      }
    : null

  const rawConfig = normalizeConfig(experience?.ntConfig)
  const config = {
    distribution:
      Array.isArray(rawConfig.distribution) &&
      rawConfig.distribution.every((n: unknown) => typeof n === "number")
        ? (rawConfig.distribution as number[])
        : [0.5, 0.5],
    traffic: typeof rawConfig.traffic === "number" ? rawConfig.traffic : 0,
    sticky: typeof rawConfig.sticky === "boolean" ? rawConfig.sticky : false,
    components: [
      {
        type: "EntryReplacement" as const,
        baseline: { id: baselineSysId },
        variants: variants.map((v: { id: string }) => ({ id: v.id })),
      },
    ],
  }

  return {
    id,
    name,
    type,
    config,
    audience,
    variants,
  }
}

export const PersonalizationExperienceMapper = (
  experienceItems: any[],
  baselineSysId: string = "",
) => {
  const formatted = (experienceItems ?? []).map((exp: any) =>
    toExperienceEntry(exp, baselineSysId),
  )
  return formatted
    .filter((experience: any) => ExperienceMapper.isExperienceEntry(experience))
    .map((experience: any) => ExperienceMapper.mapExperience(experience))
}
